import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, ApiError } from './api';

interface AuthContextValue {
  authenticated: boolean;
  loading: boolean;
  login: (password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      await api.get('/api/auth/me');
      setAuthenticated(true);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        setAuthenticated(false);
      } else {
        setAuthenticated(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (password: string) => {
    await api.post('/api/auth/login', { password });
    setAuthenticated(true);
  }, []);

  const logout = useCallback(async () => {
    await api.post('/api/auth/logout');
    setAuthenticated(false);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ authenticated, loading, login, logout }),
    [authenticated, loading, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
